import React, {useId, useState} from 'react';
import {ChevronDownIcon, ChevronUpIcon} from '../icons';
import {
    ACTIVITY_LABELS, DURATION_LABELS, OutfitActivity, OutfitContextParams, OutfitDuration,
    OutfitPreference, OutfitTransport, PREFERENCE_LABELS, TRANSPORT_LABELS, hasContextParams,
} from '../../support/outfitContext';

interface Props {
    value: OutfitContextParams;
    onChange: (next: OutfitContextParams) => void;
}

/** Группа чипов: повторное нажатие на выбранный чип снимает выбор. */
function Group<T extends string>({title, labels, value, onSelect, testId}: {
    title: string;
    labels: Record<T, string>;
    value: T | null;
    onSelect: (next: T | null) => void;
    testId: string;
}) {
    return (
        <div>
            <h4 className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">{title}</h4>
            <div className="flex flex-wrap gap-2" data-testid={testId}>
                {(Object.keys(labels) as T[]).map(code => {
                    const active = value === code;
                    return (
                        <button key={code} type="button" aria-pressed={active}
                                data-testid={`${testId}-${code}`}
                                onClick={() => onSelect(active ? null : code)}
                                className={`min-h-[44px] px-3 rounded-lg text-sm border transition-colors ${active
                                    ? 'bg-brand-50 dark:bg-brand-900/30 border-brand-400 text-brand-700 dark:text-brand-300 font-medium'
                                    : 'border-gray-200 dark:border-ink-700 text-gray-600 dark:text-gray-300 hover:border-brand-300'}`}>
                            {labels[code]}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Расширенная панель параметров подбора (CTHS-161).
 *
 * Свёрнута по умолчанию намеренно: первый экран отвечает на вопрос «куда», а не просит
 * заполнить форму. Развёрнутой панель открывается только тогда, когда параметры уже заданы —
 * например, их подставил сценарный тайл, и пользователь вправе увидеть, что именно выбрано.
 */
const OutfitContextPanel: React.FC<Props> = ({value, onChange}) => {
    const [open, setOpen] = useState(() => hasContextParams(value));
    const panelId = useId();
    const selected = [value.activity, value.transport, value.duration, value.preference]
        .filter(Boolean).length;

    return (
        <div className="rounded-2xl border border-gray-200 dark:border-ink-700">
            <button type="button" onClick={() => setOpen(v => !v)}
                    aria-expanded={open} aria-controls={panelId}
                    data-testid="context-panel-toggle"
                    className="w-full min-h-[44px] px-4 py-2 flex items-center justify-between gap-2 text-left">
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Дополнительные параметры
                    {selected > 0 && (
                        <span className="ml-2 text-xs font-normal text-brand-600 dark:text-brand-300"
                              data-testid="context-panel-count">
                            выбрано: {selected}
                        </span>
                    )}
                </span>
                {open
                    ? <ChevronUpIcon className="w-4 h-4 text-gray-400 shrink-0"/>
                    : <ChevronDownIcon className="w-4 h-4 text-gray-400 shrink-0"/>}
            </button>
            <div id={panelId} hidden={!open} className="px-4 pb-4 space-y-3"
                 data-testid="context-panel-body">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    Необязательно. Каждый параметр меняет подбор предсказуемо: велосипед исключает
                    каблуки и открытую обувь, долгая ходьба требует закрытой, «весь день» добавляет
                    запас по теплу.
                </p>
                <Group<OutfitActivity> title="Активность" labels={ACTIVITY_LABELS}
                                       value={value.activity} testId="context-activity"
                                       onSelect={next => onChange({...value, activity: next})}/>
                <Group<OutfitTransport> title="Транспорт" labels={TRANSPORT_LABELS}
                                        value={value.transport} testId="context-transport"
                                        onSelect={next => onChange({...value, transport: next})}/>
                <Group<OutfitDuration> title="Длительность" labels={DURATION_LABELS}
                                       value={value.duration} testId="context-duration"
                                       onSelect={next => onChange({...value, duration: next})}/>
                <Group<OutfitPreference> title="Предпочтение на сегодня" labels={PREFERENCE_LABELS}
                                         value={value.preference} testId="context-preference"
                                         onSelect={next => onChange({...value, preference: next})}/>
            </div>
        </div>
    );
};

export default OutfitContextPanel;
